import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { appConfig } from "@/config/app";
import { isMsalActive } from "@/config/auth";
import { CurrentUserContext, type CurrentUser } from "./currentUserContext";
import { fetchMeWithRetry } from "./fetchMeWithRetry";

// Mirrors the backend's dev claims so local work isn't gated on Entra provisioning.
const DEV_USER: CurrentUser = {
  userId: "dev-bypass-user",
  userEmail: "dev-bypass@klyra.local",
  roles: ["App.ADMIN"],
};

// app_service: identity + roles from the MSAL account; /me call JIT-registers the
// user and surfaces provider IDs. Must only render under MsalProvider.
function MsalCurrentUserProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const [providerIds, setProviderIds] = useState<string[] | undefined>(undefined);

  const accountKey = accounts[0]?.homeAccountId ?? null;

  const loadProviderIds = useCallback(async (signal?: AbortSignal) => {
    const res = await fetchMeWithRetry({ signal });
    if (signal?.aborted) return;
    setProviderIds(res?.provider_ids ?? []);
  }, []);

  useEffect(() => {
    // Dev bypass has no real MSAL account to key off, but the backend still
    // injects a dev user (KODEX_ENV=development) and resolves its real
    // provider_ids from kodex_user_provider_access -- fetch on mount so
    // provider-scoped UI (upload, schema builder) has real data locally.
    if (!appConfig.flags.devAuthBypass && !accountKey) return;

    const controller = new AbortController();
    // Keep providerIds undefined while retrying so ProviderSelector stays in
    // loading rather than treating a transient outage as “no providers.”
    // On total failure, resolve to [] (not leave undefined) so the selector
    // exits “Loading providers…” — empty array means “resolved, none.”
    void loadProviderIds(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accountKey, loadProviderIds]);

  const refreshCurrentUser = useCallback(async () => {
    await loadProviderIds();
  }, [loadProviderIds]);

  const user = useMemo<CurrentUser | null>(() => {
    if (appConfig.flags.devAuthBypass) return { ...DEV_USER, providerIds };
    const account = accounts[0] ?? null;
    if (!account) return null;
    const rawRoles = account.idTokenClaims?.roles;
    const roles = Array.isArray(rawRoles)
      ? rawRoles.filter((r): r is string => typeof r === "string")
      : [];
    return {
      userId: account.localAccountId || account.homeAccountId,
      userEmail: account.username,
      roles,
      providerIds,
    };
  }, [accounts, providerIds]);

  const value = useMemo(() => ({ user, refreshCurrentUser }), [user, refreshCurrentUser]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

// databricks_apps: no MSAL. Identity, roles, and provider IDs all come from /me
// (which also JIT-registers the user).
function AppsCurrentUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);

  const loadUser = useCallback(async (signal?: AbortSignal) => {
    const res = await fetchMeWithRetry({ signal });
    if (signal?.aborted) return;
    // Resolve providerIds even when /me fails so ProviderSelector does not
    // spin forever (undefined = still loading; [] = resolved none).
    if (!res) {
      setUser({
        userId: "",
        userEmail: "",
        roles: [],
        providerIds: [],
      });
      return;
    }
    setUser({
      userId: res.user_id,
      userEmail: res.email ?? "",
      roles: res.roles ?? [],
      providerIds: res.provider_ids,
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadUser(controller.signal);
    return () => {
      controller.abort();
    };
  }, [loadUser]);

  const refreshCurrentUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const value = useMemo(() => ({ user, refreshCurrentUser }), [user, refreshCurrentUser]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  return isMsalActive ? (
    <MsalCurrentUserProvider>{children}</MsalCurrentUserProvider>
  ) : (
    <AppsCurrentUserProvider>{children}</AppsCurrentUserProvider>
  );
}
